/* eslint react/no-find-dom-node: 0 */
import React, { Component, createRef } from 'react'
import { map, isUndefined, isEqual, groupBy, mapValues } from 'libraries/lodash'
import cx from 'classnames'
import Sticky from 'react-sticky-el'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import MuiButton from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import { dismissPopover } from 'services/OverlayService'
import BooleanFilter from './BooleanFilter'
import DateFilter from './DateRangeFilter'
import NumberFilter from './NumberFilter'
import TextFilter from './TextFilter'
import RestrictingValuesFilter from './RestrictingValuesFilter'

export class Filters extends Array {
  add = (name, value, operator) => {
    return new Filters(
      ...this.remove(name, operator),
      {
        name,
        operator,
        value
      }
    )
  }

  remove = (name, operator) => {
    return new Filters(
      ...this.filter(x => x.name !== name || (!isUndefined(operator) && x.operator !== operator))
    )
  }

  removeAll = (name) => {
    return new Filters(
      ...this.filter(x => x.name !== name)
    )
  }

  get = (name) => {
    return [
      ...this.filter(x => x.name === name)
    ]
  }

  setValues = (name, values) => {
    return values.reduce(
      (filters, [value, operator]) => value === null
        ? filters.remove(name, operator)
        : filters.add(name, value, operator),
      this
    )
  }
}

const stopPropagation = (e) => {
  dismissPopover(e.target)
  e.stopPropagation()
}

export default class FilterOverlay extends Component {
  constructor (props) {
    super(props)
    this.state = {
      filters: new Filters(...props.filterValues),
      showActiveFilters: false
    }
  }

  setFilterValue = (name) => (values) => {
    this.setState({ filters: this.state.filters.setValues(name, values) })
  }

  handleClose = () => this.props.pageToggleOverlay(null)

  handleSubmit = () => {
    const { onSubmit, pageToggleOverlay } = this.props
    onSubmit([...this.state.filters].filter(f => f.value.length))
    pageToggleOverlay(null)
  }

  handleReset = () => {
    this.setState({ filters: new Filters() })
  }

  get groups () {
    const grouped = groupBy(this.props.parameters, 'group')
    return mapValues(grouped,
      g => g.map(({ name }) => name)
    )
  }

  render () {
    const { parameters, browser } = this.props
    const { filters, showActiveFilters } = this.state
    return (
      <Modal open onClick={stopPropagation} onMouseDown={stopPropagation} onMouseUp={stopPropagation}>
        <div className='max-h-100 h-100 pa8 max-w17 mhauto outline-0'>
          <div className='mhauto pa6 flex flex-column grow-1 f6 bg-white br2 h-100'>
            <div className='w-100 flex justify-between pt4 pb3'>
              <Grid container spacing={3}>
                <Grid item>
                  <a
                    href={null}
                    className={cx('black pointer', { underline: !showActiveFilters })}
                    onClick={() => this.setState({ showActiveFilters: false })}
                  >
                    All filters
                  </a>
                </Grid>
                <Grid item>
                  <a
                    href={null}
                    className={cx('black pointer', { underline: showActiveFilters })}
                    onClick={() => this.setState({ showActiveFilters: true })}
                  >
                    Active filters
                  </a>
                </Grid>
              </Grid>
              <div>
                <IconButton color='inherit' onClick={this.handleClose} aria-label='close'>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <div className='flex relative h1 grow-1'>
              <div id='StickyAnchor' className='flex flex-column overflow-auto relative w-100 grow-1'>
                {map(this.groups, (list, title) => (
                  <ParameterGroup
                    key={title}
                    {...{ title, list, parameters, filters, browser }}
                    setFilterValue={this.setFilterValue}
                    showActiveFilters={showActiveFilters}
                  />
                ))}
              </div>
            </div>
            <OverlayFooter onClose={this.handleClose} onReset={this.handleReset} onSubmit={this.handleSubmit} />
          </div>
        </div>
      </Modal>
    )
  }
}

const Button = styled(MuiButton)({
  border: 'solid 1px #6288a5 !important',
  boxShadow: 'none',
  color: '#23527c',
  '&:hover': {
    backgroundColor: 'rgba(0,140,159,0.15)',
    color: 'black'
  }
})

const ApplyButton = styled(MuiButton)({
  border: 'solid 1px #6288a5 !important',
  backgroundColor: '#004b7a',
  color: 'white !important',
  '&:hover': {
    backgroundColor: 'rgba(0,140,159,0.15)',
    color: 'black !important'
  }
})

const OverlayFooter = ({ onClose: handleClose, onReset: handleReset, onSubmit: handleSubmit }) => (
  <div className='z15 pa4' style={{ boxShadow: '0 -1px 0 rgba(17,17,17,0.75)' }}>
    <Grid
      container
      alignItems='center'
      justifyContent='flex-end'
      className='f6'
    >
      <Grid item className='ph3'>
        <Button onClick={handleClose}>
          Cancel
        </Button>
      </Grid>
      <Grid item className='ph3'>
        <Button onClick={handleReset}>
          Reset all
        </Button>
      </Grid>
      <Grid item className='ph3'>
        <ApplyButton onClick={handleSubmit}>
          Apply
        </ApplyButton>
      </Grid>
    </Grid>
  </div>
)

const ParameterGroup = ({ title, list, parameters, filters, setFilterValue, showActiveFilters, browser }) => {
  return (
    <div>
      <Sticky
        scrollElement='#StickyAnchor'
        className='f6 b pa4 bb bt b--ghost-grey z5 bg-faf-grey'
      >
        {title}
      </Sticky>
      <div className='relative'>
        {list.map((id, index) => (
          <ParameterListItem
            key={index}
            {...parameters[id]}
            filterValue={filters.get(id)}
            setFilterValue={setFilterValue(id)}
            {...{ showActiveFilters, browser }}
          />
        ))}
      </div>
    </div>
  )
}

class ParameterListItem extends Component {
  componentRef = createRef(null)
  state = {
    minHeight: undefined
  }

  get enabled () {
    return this.props.filterValue.length > 0
  }

  get defaultOperator () {
    switch (this.props.type) {
      case 'DateParameterType':
      case 'NumberParameterType':
        return '>'
      case 'StringParameterType':
        return this.props.restrictingValues ? '=' : '≈'
      default:
        return '='
    }
  }

  handleToggleFilter = (e) => {
    this.props.setFilterValue([
      [
        this.enabled ? null : [],
        this.enabled ? undefined : this.defaultOperator
      ]
    ])
  }

  renderFilter () {
    const { props: { type, basicDisplay }, enabled } = this
    return (
      <div className={cx({
        'max-w14': type === 'DateParameterType' && basicDisplay,
        pt6: enabled
      })}
      >
        <FilterWrapper {...{ ...this.props, column: false }} />
      </div>
    )
  }

  shouldComponentUpdate (props, state) {
    return !isEqual(this.state, state) || !isEqual(this.props, props)
  }

  componentDidUpdate () {
    if (this.componentRef.current) {
      const height = () => Math.ceil(this.componentRef.current.lastChild.getBoundingClientRect().height)
      this.props.browser.name === 'ie' && this.setState({ minHeight: this.enabled ? height() + 50 : undefined })
    }
  }

  render () {
    const { props: { description, showActiveFilters }, enabled } = this
    if (showActiveFilters && !enabled) {
      return null
    }

    return (
      <div className='f5 pa6 relative bb-thin b--gains-boro' style={this.state} ref={this.componentRef}>
        <div
          className={cx('f5 dark-cyan pointer flex justify-between items-center overlay-parameter-header', {
            'overlay-parameter-header-enabled': enabled
          })}
          onClick={this.handleToggleFilter}
        >
          <div className='ml7 grow-1'>{description}</div>
          {enabled &&
            <div className='ml6'><a>Click to reset</a></div>}
        </div>
        {enabled && this.renderFilter()}
      </div>
    )
  }
}

export class FilterWrapper extends Component {
  render () {
    if (this.props.restrictingValues) {
      return <RestrictingValuesFilter {...this.props} />
    }
    switch (this.props.type) {
      case 'BooleanParameterType':
        return <BooleanFilter {...this.props} />
      case 'DateParameterType':
        return <DateFilter {...this.props} />
      case 'NumberParameterType':
        return <NumberFilter {...this.props} />
      case 'StringParameterType':
        return <TextFilter {...this.props} />
      default:
        return <div>TODO: {this.props.type}</div>
    }
  }
}
